import {
  SpaceBetween,
  Box,
  CollectionPreferences,
  Link,
  Pagination,
  Table,
  TextFilter,
  Select,
  Badge,
  ButtonDropdown,
  CollectionPreferencesProps,
  ButtonDropdownProps,
  Flashbar,
  FlashbarProps,
} from '@amzn/awsui-components-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAmbassadors } from '../../hooks/ambassadors/getAmbassadors';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { buildAmbassadorDirectoryOptions } from '../../utils/ambassadors';
import { useUser } from '../../hooks/user';
import { useSearchParams } from 'react-router-dom';
import { UpdateAmbassadorModal } from './updateAmbassadorModal';

export interface AmbassadorDirectoryRow {
  name: string;
  alt: string;
  description: string;
  type: string;
  size: string;
}

export function AmbassadorDirectoryTable({ inYourArea }: { inYourArea?: boolean }) {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const ambassadors = useGetAmbassadors();
  const loggedInUser = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    contentDisplay: [
      { id: 'name', visible: true },
      { id: 'email', visible: true },
      { id: 'country', visible: true },
      { id: 'city', visible: true },
      { id: 'representationType', visible: true },
      { id: 'siteCode', visible: true },
      { id: 'registrationDate', visible: true },
      { id: 'ergs', visible: true },
      { id: 'actions', visible: true },
    ],
  });
  const [selectedCountry, setSelectedCountry] = useState<OptionDefinition | null>(null);
  const [selectedCity, setSelectedCity] = useState<OptionDefinition | null>(null);
  const [selectedSiteCode, setSelectedSiteCode] = useState<OptionDefinition | null>(null);
  const [selectedERG, setSelectedERG] = useState<OptionDefinition | null>(null);
  const [flashbars, setFlashbars] = useState<FlashbarProps.MessageDefinition[]>([]);

  const [showingUpdateModal, setShowingUpdateModal] = useState<boolean>(false);

  const directoryActionsForSelf = [
    // TODO: connect to profile page (https://sim.amazon.com/issues/VOLUNTEERCENTER-358)
    { id: 'viewProfile', text: t('ambassadorDirectory.viewYourProfile') },
    {
      id: 'updateDetails',
      text: t('ambassadorDirectory.updateYourRegistrationDetails'),
      href: '#update',
    },
    {
      id: 'leave',
      text: t('ambassadorDirectory.leaveTheCommunityAmbassadorProgram'),
      href: '/ambassadors/offboarding',
    },
  ];

  const directoryActionsForOthers = [
    // TODO: send email for removal request (https://sim.amazon.com/issues/VOLUNTEERCENTER-375)
    { id: 'remove', text: t('ambassadorDirectory.requestRemoval') },
  ];

  const openUpdateModal = () => {
    setSearchParams(!searchParams.get('update') ? '?update=true' : '');
  };

  useEffect(() => {
    if (searchParams.get('update')) {
      setShowingUpdateModal(true);
    } else {
      setShowingUpdateModal(false);
    }
  }, [searchParams, setShowingUpdateModal]);

  const filteredItems = ambassadors.data?.filter((i) => {
    const hasSelectedSiteCodeToFilterBy = !!selectedSiteCode?.value;
    const ambassadorMatchesSelectedSiteCode = i.siteCode === selectedSiteCode?.value;

    const hasSelectedERGToFilterBy = !!selectedERG?.value;
    const ambassadorMatchesSelectedErg =
      selectedERG?.value && i.ergName?.includes(selectedERG?.value);

    const ambassadorIsInSameCityAsLoggedInUser = i.city === loggedInUser.employeeCity;
    const hasSelectedCountryToFilterBy = !!selectedCountry?.value;
    const ambassadorMatchesSelectedCountry = i.country === selectedCountry?.value;
    const hasSelectedCityToFilterBy = !!selectedCity?.value;
    const ambassadorMatchesSelectedCity = i.city === selectedCity?.value;

    return (
      // if user has selected a site code to filter by, and the current ambassador has that site code
      // or if we have not selected a site code
      ((hasSelectedSiteCodeToFilterBy && ambassadorMatchesSelectedSiteCode) ||
        !hasSelectedSiteCodeToFilterBy) &&
      // same here, checks if erg is selected and filters by it
      (ambassadorMatchesSelectedErg || !hasSelectedERGToFilterBy) &&
      // For the "In Your Area" table we filter by the logged in user's city
      ((inYourArea && ambassadorIsInSameCityAsLoggedInUser) ||
        // we only have to check country and city if user is not in the "In Your Area" table
        // because we know the city and country is the logged in user's city and country in the "In Your Area" table
        (!inYourArea &&
          ((hasSelectedCountryToFilterBy && ambassadorMatchesSelectedCountry) ||
            !hasSelectedCountryToFilterBy) &&
          ((hasSelectedCityToFilterBy && ambassadorMatchesSelectedCity) ||
            !hasSelectedCityToFilterBy)))
    );
  });

  const { items, collectionProps, filterProps, paginationProps } = useCollection(
    filteredItems || [],
    {
      filtering: {},
      pagination: { defaultPage: 0, pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [
          {
            key: 'email',
            operators: [
              {
                operator: '=',
                match: (itemValue, tokenValue) => tokenValue.split(',').includes(itemValue),
              },
              {
                operator: '!=',
                match: (itemValue, tokenValue) => !tokenValue.split(',').includes(itemValue),
              },
            ],
            groupValuesLabel: '',
            propertyLabel: '',
          },
          {
            key: 'name',
            operators: [
              {
                operator: '=',
                match: (itemValue, tokenValue) => tokenValue.split(',').includes(itemValue),
              },
              {
                operator: '!=',
                match: (itemValue, tokenValue) => !tokenValue.split(',').includes(itemValue),
              },
            ],
            groupValuesLabel: '',
            propertyLabel: '',
          },
        ],
      },
    }
  );

  const countryOptions = useMemo(() => {
    return buildAmbassadorDirectoryOptions(ambassadors.data || [], 'country');
  }, [ambassadors.data]);

  const cityOptions = useMemo(() => {
    return buildAmbassadorDirectoryOptions(ambassadors.data || [], 'city');
  }, [ambassadors.data]);

  const siteCodeOptions = useMemo(() => {
    return buildAmbassadorDirectoryOptions(ambassadors.data || [], 'siteCode');
  }, [ambassadors.data]);

  const ergOptions = useMemo(() => {
    const unique = new Set();

    if (ambassadors.data?.length) {
      for (let i = 0; i < ambassadors.data?.length; i++) {
        const obj = ambassadors.data?.[i];
        // Ensure object is non-null and has its own siteCode property
        if (obj && Object.prototype.hasOwnProperty.call(obj, 'ergName')) {
          const code = obj['ergName' as keyof typeof obj] as string[];
          // Skip undefined/null; convert to string for the final string array
          if (code != null) {
            code.forEach((c) => unique.add(String(c)));
          }
        }
      }
    }

    // Convert Set to array (in insertion order)
    return Array.from(unique)
      .sort()
      .map((u) => ({ value: u as string, label: u as string }));
  }, [ambassadors.data]);

  const onDropdownItemClick = (item: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
    if (item.detail.id === 'updateDetails') {
      openUpdateModal();
    }
  };

  const dataUpdatedFlash = {
    type: 'success',
    dismissible: true,
    dismissLabel: t('ambassadorDirectory.dismissMessage'),
    onDismiss: () => setFlashbars([]),
    content: <>{t('ambassadorDirectory.successfulUpdate')}</>,
    id: 'message_1',
  } as FlashbarProps.MessageDefinition;

  const updateErrorFlash = {
    type: 'error',
    dismissible: true,
    dismissLabel: t('ambassadorDirectory.dismissMessage'),
    onDismiss: () => setFlashbars([]),
    content: <>{t('ambassadorDirectory.errorOnUpdate')}</>,
    id: 'message_2',
  } as FlashbarProps.MessageDefinition;

  const onSuccessfulUpdate = () => {
    setFlashbars([dataUpdatedFlash]);
  };

  const onUpdateError = () => {
    setFlashbars([updateErrorFlash]);
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      <Flashbar items={flashbars} />
      <UpdateAmbassadorModal
        visible={showingUpdateModal}
        setVisible={openUpdateModal}
        onSuccess={onSuccessfulUpdate}
        onError={onUpdateError}
      />
      <Table
        {...collectionProps}
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        columnDefinitions={[
          {
            id: 'name',
            header: t('ambassadorDirectory.name'),
            cell: (item) => `${item.firstName} ${item.lastName}`,
            sortingField: 'name',
            sortingComparator: (a, b) => {
              return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
            },
            isRowHeader: true,
          },
          {
            id: 'email',
            header: t('ambassadorDirectory.email'),
            cell: (item) => <Link href="#">{item.email || '-'}</Link>,
            sortingField: 'email',
          },
          {
            id: 'country',
            header: t('ambassadorDirectory.country'),
            cell: (item) => item.country || '-',
            sortingField: 'country',
          },
          {
            id: 'city',
            header: t('ambassadorDirectory.city'),
            cell: (item) => item.city || '-',
            sortingField: 'city',
          },
          {
            id: 'representationType',
            header: t('ambassadorDirectory.representationType'),
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xxs">
                {item.representationTypes?.map((representation) => {
                  // undefined color is gray which is what we want for SITE
                  const color =
                    representation === 'SITE'
                      ? undefined
                      : representation === 'ELG'
                        ? 'green'
                        : 'blue';
                  return <Badge color={color}>{representation}</Badge>;
                })}
              </SpaceBetween>
            ),
            sortingField: 'representationType',
            sortingComparator: (a, b) => {
              const aSorted = [...(a.representationTypes || [])].sort((x, y) => x.localeCompare(y));
              const bSorted = [...(b.representationTypes || [])].sort((x, y) => x.localeCompare(y));
              const minLen = Math.min(aSorted.length, bSorted.length);
              for (let i = 0; i < minLen; i++) {
                const cmp = aSorted[i].localeCompare(bSorted[i]);
                if (cmp !== 0) return cmp;
              }
              return aSorted.length - bSorted.length;
            },
          },
          {
            id: 'siteCode',
            header: t('ambassadorDirectory.siteCode'),
            cell: (item) => item.siteCode || '-',
            sortingField: 'siteCode',
          },
          {
            id: 'registrationDate',
            header: t('ambassadorDirectory.registrationDate'),
            cell: (item) => item.lastOnboardedDate.toDateString() || '-',
            sortingField: 'registrationDate',
            sortingComparator: (a, b) =>
              a.lastOnboardedDate.getTime() - b.lastOnboardedDate.getTime(),
          },
          {
            id: 'ergs',
            header: t('ambassadorDirectory.ELG'),
            cell: (item) => item.ergName?.join(', '),
            sortingField: 'ergs',
          },
          {
            id: 'actions',
            header: t('ambassadorDirectory.actions'),
            cell: (item) => (
              <ButtonDropdown
                items={
                  item.personId === loggedInUser.employeePersonId
                    ? directoryActionsForSelf
                    : directoryActionsForOthers
                }
                onItemClick={onDropdownItemClick}
                expandToViewport
                ariaLabel="Control instance"
                variant="icon"
              />
            ),
            minWidth: 170,
          },
        ]}
        loading={ambassadors.isLoading}
        enableKeyboardNavigation
        items={items}
        filter={
          <SpaceBetween direction="horizontal" size="s" alignItems="end">
            <TextFilter
              {...filterProps}
              filteringPlaceholder={t('ambassadorDirectory.searchByNameOrAlias')}
            />
            {!inYourArea && (
              <>
                <Select
                  data-directory-dropdown
                  selectedOption={selectedCountry}
                  placeholder={t('ambassadorDirectory.country')}
                  inlineLabelText={t('ambassadorDirectory.country')}
                  onChange={({ detail }) => {
                    detail.selectedOption.value !== selectedCountry?.value
                      ? setSelectedCountry(detail.selectedOption)
                      : setSelectedCountry(null);
                  }}
                  options={countryOptions}
                />
                <Select
                  selectedOption={selectedCity}
                  placeholder={t('ambassadorDirectory.city')}
                  inlineLabelText={t('ambassadorDirectory.city')}
                  onChange={({ detail }) => {
                    detail.selectedOption.value !== selectedCity?.value
                      ? setSelectedCity(detail.selectedOption)
                      : setSelectedCity(null);
                  }}
                  options={cityOptions}
                />
              </>
            )}
            <Select
              selectedOption={selectedSiteCode}
              inlineLabelText={t('ambassadorDirectory.siteCode')}
              placeholder={t('ambassadorDirectory.siteCode')}
              onChange={({ detail }) => {
                detail.selectedOption.value !== selectedSiteCode?.value
                  ? setSelectedSiteCode(detail.selectedOption)
                  : setSelectedSiteCode(null);
              }}
              options={siteCodeOptions}
            />
            <Select
              selectedOption={selectedERG}
              placeholder={t('ambassadorDirectory.ELG')}
              inlineLabelText={t('ambassadorDirectory.ELG')}
              onChange={({ detail }) => {
                detail.selectedOption.value !== selectedERG?.value
                  ? setSelectedERG(detail.selectedOption)
                  : setSelectedERG(null);
              }}
              options={ergOptions}
            />
          </SpaceBetween>
        }
        columnDisplay={preferences.contentDisplay}
        preferences={
          <CollectionPreferences
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            contentDisplayPreference={{
              title: t('ambassadorDirectory.columnPreferences'),
              options: [
                { id: 'name', label: t('ambassadorDirectory.name') },
                { id: 'email', label: t('ambassadorDirectory.email') },
                { id: 'country', label: t('ambassadorDirectory.country') },
                { id: 'city', label: t('ambassadorDirectory.city') },
                { id: 'representationType', label: t('ambassadorDirectory.representationType') },
                { id: 'siteCode', label: t('ambassadorDirectory.siteCode') },
                { id: 'registrationDate', label: t('ambassadorDirectory.registrationDate') },
                { id: 'ergs', label: t('ambassadorDirectory.ELG') },
                { id: 'actions', label: t('ambassadorDirectory.actions') },
              ],
            }}
            pageSizePreference={{
              title: t('ambassadorDirectory.pageSize'),
              options: [
                { value: 10, label: `10 ${t('ambassadorDirectory.items')}` },
                { value: 20, label: `20 ${t('ambassadorDirectory.items')}` },
                { value: 30, label: `30 ${t('ambassadorDirectory.items')}` },
                { value: 40, label: `40 ${t('ambassadorDirectory.items')}` },
              ],
            }}
            confirmLabel={t('ambassadorDirectory.save')}
            cancelLabel={t('ambassadorDirectory.cancel')}
          />
        }
        loadingText={t('ambassadorDirectory.loadingResources')}
        pagination={<Pagination {...paginationProps} />}
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>{t('ambassadorDirectory.noResources')}</b>
            </SpaceBetween>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
